import { EnumContainerActionRequest } from '@Model';
import { FlexColumn, FlexRow, FloatingCard, renderDateTime, useTranslate } from '@synqo/frontend-component-library';
import { type ContainerHistoryEntry } from './containerHistory';
import classes from './ContainersAtSite.module.css';
import { OrderLink } from './OrderLink';

const VERBS: Record<EnumContainerActionRequest, { kind: string; text: string }> = {
    [EnumContainerActionRequest.Place]: { kind: 'place', text: 'gestellt' },
    [EnumContainerActionRequest.Collect]: { kind: 'collect', text: 'abgeholt' },
    [EnumContainerActionRequest.Empty]: { kind: 'empty', text: 'geleert' },
    [EnumContainerActionRequest.Exchange]: { kind: 'swap', text: 'getauscht' },
};

interface ContainerHistoryCardProps {
    entries: ContainerHistoryEntry[];
    failed?: boolean;
    onOpenOrder: (orderId: string) => void;
}

/** The latest container actions at a site, newest first, alike containers of an order as one line. */
// @ai-generated 2026-10-13T14:20:49+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function ContainerHistoryCard({ entries, failed = false, onOpenOrder }: ContainerHistoryCardProps) {
    const i18n = useTranslate('');

    return (
        <FloatingCard title={i18n('Zuletzt hier')}>
            {failed && (
                <FlexRow className={classes.warning}>
                    {i18n('Die letzten Vorgänge konnten nicht geladen werden.')}
                </FlexRow>
            )}
            {!failed && entries.length === 0 && (
                <FlexRow className={classes.hint}>{i18n('Noch kein Vorgang.')}</FlexRow>
            )}
            {entries.length > 0 && (
                <FlexColumn label={i18n('Zuletzt hier')} gap="xxs" align="stretch" className={classes.history}>
                    {entries.map((entry) => (
                        <HistoryEntry key={entry.key} entry={entry} onOpenOrder={onOpenOrder} />
                    ))}
                </FlexColumn>
            )}
        </FloatingCard>
    );
}

// @ai-generated 2026-10-13T14:20:49+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function HistoryEntry({
    entry,
    onOpenOrder,
}: {
    entry: ContainerHistoryEntry;
    onOpenOrder: (orderId: string) => void;
}) {
    const i18n = useTranslate('');
    const verb = VERBS[entry.action];

    return (
        <FlexRow gap="xs" align="baseline" wrap="wrap">
            <FlexRow className={classes.date}>{renderDateTime(entry.date, 'date')}</FlexRow>
            <FlexRow className={classes[verb.kind]}>{i18n(verb.text)}</FlexRow>
            {describeContainers(entry)}
            <OrderLink orderId={entry.order.id} number={entry.order.number} onOpen={onOpenOrder} />
        </FlexRow>
    );
}

// @ai-generated 2026-10-13T14:20:49+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function describeContainers(entry: ContainerHistoryEntry): string {
    const count = entry.count > 1 ? `${entry.count} × ` : '';
    const number = entry.container.display_string;
    const swapped = entry.swappedFor ? ` → Nr. ${entry.swappedFor}` : '';

    return `${count}${entry.containerType.display_string}${number ? ` Nr. ${number}` : ''}${swapped}`;
}
